import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, MoreHorizontal, UserMinus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import ProfileThumbnail from "../ProfileThumbnail";
import { Chip } from "../Sticker";
import { attendanceLabel, attendanceStatus } from "../../lib/rsvp";
import {
  buildHostWhatsAppGreeting,
  openFeeWhatsApp,
  phoneToWaMe,
} from "../../lib/schoolFeeMessages";

export default function DashboardGuestRow({
  r, tentative = false, removing = false, onRequestRemove,
  isHost = false, onMakeCoAdmin, promoting = false,
  hostName = "", schoolName = "",
}) {
  const roleChipColor = { Leader: "sky", Follower: "pink", Both: "amber" }[r.role] || "amber";
  const status = attendanceStatus(r);
  const hasPhone = Boolean(phoneToWaMe(r.phone));
  const showCoAdmin = Boolean(isHost && onMakeCoAdmin);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    function onPointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    function onKeyDown(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  function messageWhatsApp() {
    if (!hasPhone) {
      toast.error("No phone on file");
      return;
    }
    const message = buildHostWhatsAppGreeting({ hostName, schoolName });
    const ok = openFeeWhatsApp({ phone: r.phone, message });
    if (!ok) toast.error("No phone on file");
  }

  const itemClass =
    "w-full inline-flex items-center gap-2 font-ui font-medium text-[14px] text-left px-3 py-2.5 min-h-[44px] rounded-sm hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum disabled:opacity-50 disabled:cursor-not-allowed";
  const dangerItemClass =
    "w-full inline-flex items-center gap-2 font-ui font-medium text-[14px] text-left text-red-700 px-3 py-2.5 min-h-[44px] rounded-sm hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum disabled:opacity-50 disabled:cursor-not-allowed";
  const iconBtnClass =
    "inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md2 border border-black/[0.08] bg-white text-ink shadow-none transition-colors hover:bg-cream active:bg-[#f0ecef] focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div
      className="rounded-md2 border border-black/[0.08] p-3 flex flex-col gap-3 sm:flex-row sm:items-center bg-white"
      data-testid={`dash-row-${r.id}`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0 w-full">
        <ProfileThumbnail
          name={r.name}
          role={r.role}
          thumbnailUrl={r.thumbnail_url}
          hidden={r.hide_name}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-ui font-semibold text-[15px] truncate">
              {r.name}
              {r.hide_name && <span className="ml-2 font-ui font-medium text-[12px] text-ink/60">(private on public list)</span>}
            </p>
            {r.first_timer && <Chip color="pink">First timer</Chip>}
            {tentative && (
              <Chip color={status === "not_coming" ? "ink" : "amber"}>
                {attendanceLabel(status)}
              </Chip>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            {r.role && <Chip color={roleChipColor}>{r.role}</Chip>}
            {(r.dance_forms || []).map((d) => (
              <Chip key={`${d.name}-${d.level}`} color="white">{d.name} · {d.level}</Chip>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 sm:shrink-0 self-end sm:self-auto">
        {hasPhone && (
          <button
            type="button"
            onClick={messageWhatsApp}
            className={iconBtnClass}
            aria-label="Message on WhatsApp"
            title="Message on WhatsApp"
            data-testid={`dash-whatsapp-${r.dancer_id}-btn`}
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.5} aria-hidden />
          </button>
        )}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className={iconBtnClass}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label="More guest actions"
            data-testid={`dash-guest-more-${r.dancer_id}-btn`}
          >
            <MoreHorizontal className="w-5 h-5" strokeWidth={2.5} aria-hidden />
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full mt-2 min-w-[12.5rem] rounded-md2 border border-black/[0.08] bg-white p-1 shadow-sm flex flex-col z-50"
              data-testid={`dash-guest-more-menu-${r.dancer_id}`}
            >
              {showCoAdmin && (
                <button
                  type="button"
                  role="menuitem"
                  disabled={promoting}
                  onClick={() => {
                    setMenuOpen(false);
                    onMakeCoAdmin(r);
                  }}
                  className={itemClass}
                  title="Grant event co-admin access"
                  data-testid={`dash-coadmin-${r.dancer_id}-btn`}
                >
                  <UserPlus className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  {promoting ? "Promoting…" : "Make co-admin"}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                disabled={removing}
                onClick={() => {
                  setMenuOpen(false);
                  onRequestRemove();
                }}
                className={dangerItemClass}
                title="Remove this dancer from the guest list (for example after a refund)"
                data-testid={`dash-remove-${r.dancer_id}-btn`}
              >
                <UserMinus className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                {removing ? "Removing…" : "Remove RSVP"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
